/** Reloj circular estilo Google Calendar / Material TimePicker. */

import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

const OUTER_HOURS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const INNER_HOURS = [0, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];
const MINUTE_STEPS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

const SIZE = 280;
const CX = SIZE / 2;
const CY = SIZE / 2;
const R_OUTER = 108;
const R_INNER = 72;
const R_MINUTE = 108;
const R_DOT = 92;

function pad(n) {
  return String(n).padStart(2, "0");
}

export function parseHoraValue(value) {
  const m = String(value || "").match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return { h: 9, min: 0 };
  return {
    h: Math.min(23, Math.max(0, parseInt(m[1], 10) || 0)),
    min: Math.min(59, Math.max(0, parseInt(m[2], 10) || 0)),
  };
}

export function formatHoraValue(h, min) {
  return `${pad(h)}:${pad(min)}`;
}

/** Ángulo en radianes: minuto 0 arriba, sentido horario. */
function minuteToAngle(min) {
  return ((min / 60) * 360 - 90) * (Math.PI / 180);
}

function indexToAngle(i) {
  return (i * 30 - 90) * (Math.PI / 180);
}

function polar(cx, cy, r, angleRad) {
  return { x: cx + r * Math.cos(angleRad), y: cy + r * Math.sin(angleRad) };
}

function hourToAngle(h) {
  const innerIdx = INNER_HOURS.indexOf(h);
  if (innerIdx >= 0) return indexToAngle(innerIdx);
  if (h === 12) return indexToAngle(0);
  const outerIdx = OUTER_HOURS.indexOf(h);
  return indexToAngle(outerIdx >= 0 ? outerIdx : 0);
}

function RelojCircular({
  mode,
  hour,
  minute,
  onPickHour,
  onPickMinute,
  accentColor,
  accentBg,
  c,
  isDark,
}) {
  const activeAngle = mode === "hour" ? hourToAngle(hour) : minuteToAngle(minute);
  const handRadius = mode === "hour"
    ? (INNER_HOURS.includes(hour) ? R_INNER - 20 : R_OUTER - 22)
    : R_MINUTE - 22;
  const handEnd = polar(CX, CY, handRadius, activeAngle);

  const dotMinutes = [];
  for (let m = 0; m < 60; m += 1) {
    if (m % 5 !== 0) dotMinutes.push(m);
  }

  const numBtn = (active, isInner = false) => ({
    position: "absolute",
    transform: "translate(-50%, -50%)",
    width: isInner ? 32 : 36,
    height: isInner ? 32 : 36,
    borderRadius: "50%",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: isInner ? 12 : 14,
    fontWeight: active ? 700 : 500,
    cursor: "pointer",
    fontFamily: "inherit",
    background: active ? accentColor : "transparent",
    color: active ? "#ffffff" : c.textPrimary,
    boxShadow: active ? `0 2px 8px ${accentColor}66` : "none",
    zIndex: active ? 3 : 2,
  });

  const dotBtn = (active) => ({
    position: "absolute",
    transform: "translate(-50%, -50%)",
    width: active ? 10 : 7,
    height: active ? 10 : 7,
    borderRadius: "50%",
    border: "none",
    padding: 0,
    cursor: "pointer",
    background: active ? accentColor : (isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.28)"),
    zIndex: active ? 3 : 1,
  });

  return (
    <div
      style={{
        position: "relative",
        width: SIZE,
        height: SIZE,
        margin: "0 auto",
        borderRadius: "50%",
        background: isDark ? "rgba(255,255,255,0.04)" : "rgba(124,58,237,0.06)",
      }}
    >
      <svg width={SIZE} height={SIZE} style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        <circle cx={CX} cy={CY} r={R_OUTER} fill="none" stroke={c.border} strokeWidth={1} opacity={0.35} />
        <circle cx={CX} cy={CY} r={R_INNER} fill="none" stroke={c.border} strokeWidth={1} opacity={0.25} />
        <line
          x1={CX}
          y1={CY}
          x2={handEnd.x}
          y2={handEnd.y}
          stroke={accentColor}
          strokeWidth={2}
          strokeLinecap="round"
        />
        <circle cx={CX} cy={CY} r={5} fill={accentColor} />
      </svg>

      {mode === "hour" && (
        <>
          {OUTER_HOURS.map((hr, i) => {
            const { x, y } = polar(CX, CY, R_OUTER, indexToAngle(i));
            const hVal = hr;
            const active = hour >= 1 && hour <= 12 && hour === hVal;
            return (
              <button
                key={`oh-${hr}`}
                type="button"
                style={{ ...numBtn(active), left: x, top: y }}
                onClick={() => onPickHour(hVal)}
              >
                {hr}
              </button>
            );
          })}
          {INNER_HOURS.map((hr, i) => {
            const { x, y } = polar(CX, CY, R_INNER, indexToAngle(i));
            const active = hour === hr;
            return (
              <button
                key={`ih-${hr}`}
                type="button"
                style={{ ...numBtn(active, true), left: x, top: y }}
                onClick={() => onPickHour(hr)}
              >
                {pad(hr)}
              </button>
            );
          })}
        </>
      )}

      {mode === "minute" && (
        <>
          {MINUTE_STEPS.map((mi, i) => {
            const { x, y } = polar(CX, CY, R_MINUTE, indexToAngle(i));
            const active = minute === mi;
            return (
              <button
                key={`m-${mi}`}
                type="button"
                style={{ ...numBtn(active), left: x, top: y }}
                onClick={() => onPickMinute(mi, true)}
              >
                {pad(mi)}
              </button>
            );
          })}
          {dotMinutes.map((mi) => {
            const { x, y } = polar(CX, CY, R_DOT, minuteToAngle(mi));
            const active = minute === mi;
            return (
              <button
                key={`d-${mi}`}
                type="button"
                title={`:${pad(mi)}`}
                aria-label={`Minuto ${mi}`}
                style={{ ...dotBtn(active), left: x, top: y }}
                onClick={() => onPickMinute(mi, true)}
              />
            );
          })}
        </>
      )}
    </div>
  );
}

export default function CitaSelectorHora({
  value,
  onChange,
  c,
  isDark,
  accentColor = "#06b6d4",
  accentBg,
  chipText,
  borderColor,
  onFocus,
  onBlur,
  align = "left",
  zIndex = 10060,
  id,
  disabled = false,
}) {
  const parsed = parseHoraValue(value);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("hour");
  const [draftH, setDraftH] = useState(parsed.h);
  const [draftM, setDraftM] = useState(parsed.min);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const close = useCallback(() => {
    setOpen(false);
    onBlur?.();
  }, [onBlur]);

  const openPanel = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const panelW = 320;
    let left = align === "right" ? rect.right - panelW : rect.left;
    left = Math.max(8, Math.min(left, window.innerWidth - panelW - 8));
    const top = Math.min(rect.bottom + 6, window.innerHeight - 420);
    setPanelPos({ top, left });
    const p = parseHoraValue(value);
    setDraftH(p.h);
    setDraftM(p.min);
    setMode("hour");
    setOpen(true);
    onFocus?.();
  }, [align, onFocus, value]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const t = e.target;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      close();
    };
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  const pickHour = (h) => {
    setDraftH(h);
    setMode("minute");
  };

  const pickMinute = (m, finish = false) => {
    setDraftM(m);
    const next = formatHoraValue(draftH, m);
    onChange(next);
    if (finish) close();
  };

  const panel = open ? (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Elegir hora"
      style={{
        position: "fixed",
        top: panelPos.top,
        left: panelPos.left,
        zIndex,
        width: 320,
        padding: "12px 12px 16px",
        borderRadius: 16,
        border: `1px solid ${c.border}`,
        background: isDark ? c.bgCard : "#ffffff",
        boxShadow: "0 16px 48px rgba(0,0,0,0.4)",
      }}
    >
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        marginBottom: 12,
        padding: "8px 12px",
        borderRadius: 12,
        background: accentBg || (isDark ? "rgba(124,58,237,0.2)" : "rgba(124,58,237,0.1)"),
      }}
      >
        <button
          type="button"
          onClick={() => setMode("hour")}
          style={{
            fontSize: 32,
            fontWeight: mode === "hour" ? 700 : 400,
            fontFamily: "inherit",
            border: "none",
            background: "transparent",
            color: mode === "hour" ? accentColor : c.textMuted,
            cursor: "pointer",
            padding: "0 4px",
          }}
        >
          {pad(draftH)}
        </button>
        <span style={{ fontSize: 28, fontWeight: 300, color: c.textMuted }}>:</span>
        <button
          type="button"
          onClick={() => setMode("minute")}
          style={{
            fontSize: 32,
            fontWeight: mode === "minute" ? 700 : 400,
            fontFamily: "inherit",
            border: "none",
            background: "transparent",
            color: mode === "minute" ? accentColor : c.textMuted,
            cursor: "pointer",
            padding: "0 4px",
          }}
        >
          {pad(draftM)}
        </button>
      </div>

      <RelojCircular
        mode={mode}
        hour={draftH}
        minute={draftM}
        onPickHour={pickHour}
        onPickMinute={pickMinute}
        accentColor={accentColor}
        accentBg={accentBg}
        c={c}
        isDark={isDark}
      />

      <div style={{ marginTop: 10, fontSize: 10, color: c.textMuted, textAlign: "center", lineHeight: 1.4 }}>
        {mode === "hour"
          ? "Toca la hora en el reloj (anillo exterior 1–12, interior 00 y 13–23)"
          : "Minutos de 5 en 5 · puntos para afinar"}
      </div>
    </div>
  ) : null;

  return (
    <>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        disabled={disabled}
        onClick={() => { if (!disabled) (open ? close() : openPanel()); }}
        style={{
          width: "100%",
          padding: "10px 8px",
          borderRadius: 10,
          border: `2px solid ${open ? accentColor : borderColor}`,
          background: c.bgInput,
          color: chipText || c.textPrimary,
          fontSize: 13,
          fontWeight: 700,
          fontFamily: "inherit",
          cursor: disabled ? "not-allowed" : "pointer",
          textAlign: "center",
          outline: "none",
          opacity: disabled ? 0.6 : 1,
        }}
      >
        {formatHoraValue(parsed.h, parsed.min)}
      </button>
      {panel && createPortal(panel, document.body)}
    </>
  );
}
